import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  CalendarCheck,
  Globe,
  Link2,
  MessageCircle,
  Plus,
  Sparkles,
  Users,
  Workflow,
  Zap,
} from "lucide-react";
import type { ComponentType } from "react";
import { useState } from "react";
import {
  AGENT_GOAL_LABEL,
  FLOW_STATUS_LABEL,
  type AgentGoalValue,
  type AgentSummary,
} from "@rn/contracts";
import { CreateAgentDialog } from "@/components/agent/CreateAgentDialog";
import { PageBody, PageHeader } from "@/components/crm/AppShell";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Hint } from "@/components/ui/tooltip";
import { relativeTime } from "@/lib/crm";
import { numberLabel, useAgents, useCompanyNumbers, type CompanyNumber } from "@/lib/agent";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/_authenticated/agente-ia/")({
  component: AgentesPage,
});

const TOM_STATUS: Record<AgentSummary["status"], NonNullable<BadgeProps["variant"]>> = {
  published: "success",
  draft: "muted",
  archived: "outline",
};

const GOAL_ICON: Record<AgentGoalValue, ComponentType<{ className?: string }>> = {
  agendar: CalendarCheck,
  qualificar: Users,
  link: Link2,
};

/**
 * Agente de IA: o atendente de WhatsApp e do site configurado em 3 passos.
 *
 * A lista responde "quem está atendendo, onde e quanto": situação, números
 * em que ele responde e conversas da semana. Cada agente é um Typechat por
 * baixo — o link "editor avançado" abre o mesmo fluxo no quadro de blocos.
 */
function AgentesPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const gestao = can("admin", "manager");
  const [criando, setCriando] = useState(false);
  const agentes = useAgents();
  const numeros = useCompanyNumbers();
  const porId = new Map((numeros.data ?? []).map((n) => [n.id, n]));
  const lista = agentes.data ?? [];

  if (!gestao) {
    return (
      <EmptyState
        icon={Sparkles}
        title="Só a gestão configura agentes"
        description="Peça a um administrador ou gestor para criar ou ajustar o agente de IA."
      />
    );
  }

  return (
    <>
      <PageHeader
        title="Agente de IA"
        description={
          lista.length > 0
            ? `${lista.filter((a) => a.status === "published").length} no ar de ${lista.length} · conversas dos últimos 7 dias`
            : "Um atendente de IA para o WhatsApp e o site, configurado em 3 passos"
        }
        action={
          <Button onClick={() => setCriando(true)}>
            <Plus />
            Criar agente
          </Button>
        }
      />

      <PageBody className="space-y-4">
        <Card className="overflow-hidden">
          {agentes.isPending ? (
            <div className="divide-y" aria-busy>
              {Array.from({ length: 3 }, (_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3.5">
                  <Skeleton className="size-9 rounded-lg" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-48 max-w-[60%]" />
                    <Skeleton className="h-3 w-28" />
                  </div>
                  <Skeleton className="hidden h-5 w-24 rounded-full md:block" />
                </div>
              ))}
            </div>
          ) : agentes.isError ? (
            <EmptyState
              icon={Sparkles}
              title="Não foi possível carregar os agentes"
              description="Confira a conexão e tente de novo."
              action={
                <Button variant="outline" size="sm" onClick={() => void agentes.refetch()}>
                  Tentar de novo
                </Button>
              }
            />
          ) : lista.length === 0 ? (
            <EmptyState
              icon={Sparkles}
              title="Nenhum agente ainda"
              description="Diga como ele se comporta, o que precisa descobrir e onde buscar as respostas. Ele atende no WhatsApp e no site, agenda na agenda real, passa para a equipe quando precisa — e cada conversa vira lead no CRM com a campanha de onde veio."
              action={
                <Button onClick={() => setCriando(true)}>
                  <Sparkles />
                  Criar o primeiro agente
                </Button>
              }
            />
          ) : (
            <ul className="divide-y">
              {lista.map((a) => (
                <Linha
                  key={a.id}
                  agente={a}
                  numeros={a.whatsappAccountIds.flatMap((id) => {
                    const n = porId.get(id);
                    return n ? [n] : [];
                  })}
                  onOpen={() =>
                    void navigate({ to: "/agente-ia/$flowId", params: { flowId: a.id } })
                  }
                />
              ))}
            </ul>
          )}
        </Card>
      </PageBody>

      <CreateAgentDialog open={criando} onOpenChange={setCriando} />
    </>
  );
}

function Linha({
  agente: a,
  numeros,
  onOpen,
}: {
  agente: AgentSummary;
  numeros: CompanyNumber[];
  onOpen: () => void;
}) {
  const Icone = GOAL_ICON[a.goal];
  return (
    <li
      className="flex cursor-pointer flex-col gap-2 px-4 py-3 transition-colors hover:bg-muted/40 md:flex-row md:items-center md:gap-4"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("a, button")) return;
        onOpen();
      }}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Sparkles className="size-4" />
        </span>
        <div className="min-w-0">
          <Link
            to="/agente-ia/$flowId"
            params={{ flowId: a.id }}
            className="block truncate font-semibold hover:text-primary"
          >
            {a.agentName.trim() || a.name}
          </Link>
          <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">
            <Icone className="size-3 shrink-0" />
            {AGENT_GOAL_LABEL[a.goal]} · editado {relativeTime(a.updatedAt)}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 md:w-64">
        <Badge variant={TOM_STATUS[a.status]}>
          {FLOW_STATUS_LABEL[a.status]}
          {a.publishedVersion ? ` · v${a.publishedVersion}` : ""}
        </Badge>
        {a.status === "published" && a.hasUnpublishedChanges && (
          <Badge variant="warning">alterações não publicadas</Badge>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5 text-xs md:w-72">
        {numeros.length === 0 && !a.web && <span className="text-muted-foreground">sem canal</span>}
        {numeros.map((n) => (
          <Badge key={n.id} variant="outline" className="max-w-full">
            <MessageCircle className="size-3" />
            <span className="truncate">{numberLabel(n)}</span>
          </Badge>
        ))}
        {a.web && (
          <Badge variant="outline">
            <Globe className="size-3" />
            site
          </Badge>
        )}
        {a.startOnAdLead && numeros.length > 0 && (
          <Hint label="Começa sozinho quando chega lead de anúncio">
            <Badge variant="info">
              <Zap className="size-3" />
              anúncio
            </Badge>
          </Hint>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 md:w-48 md:justify-end">
        <span className="text-xs tabular-nums">
          <span className="font-semibold">{a.conversations7d.toLocaleString("pt-BR")}</span>
          <span className="text-muted-foreground">
            {a.conversations7d === 1 ? " conversa" : " conversas"}
          </span>
        </span>
        <Button asChild size="xs" variant="ghost" className="text-muted-foreground">
          <Link to="/typechat/$flowId" params={{ flowId: a.id }}>
            <Workflow />
            Editor avançado
          </Link>
        </Button>
      </div>
    </li>
  );
}
